import { useAiEditorTyping } from '@/composables/ai-upload/useAiEditorTyping';
import { useKeyboardInset } from '@/composables/useKeyboardInset';
import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';

const previewBottom = ref<number | undefined>(undefined);

// Pre-measurement fallback; 100vw is a hair wider than the gallery, so the panel errs short of the picture
const fallbackCap = 'calc(100svh - 100vw)';

// Clearance for Safari's floating compact URL bubble, which otherwise lands on the save button
const browserBubbleClearance = 48;

type AiTextEditorLayout = {
    editorPanelStyle: ComputedRef<Record<string, string>>;
    measurePreviewBottom: () => void;
    forgetPreviewBottom: () => void;
};

export function useAiTextEditorLayout(): AiTextEditorLayout {
    const { keyboardInset, visibleViewportHeight } = useKeyboardInset();
    const { isTyping } = useAiEditorTyping();

    // Focus-driven: Safari 26 resizes the layout viewport for the keyboard, so the inset alone reads 0
    const hasKeyboardBand = computed<boolean>(() => (isTyping.value || keyboardInset.value > 0) && visibleViewportHeight.value > 0);

    // Height, not current bottom: the editor slides the gallery to the top, so its height is where it will end
    function measurePreviewBottom(): void {
        if (typeof document === 'undefined') {
            return;
        }

        const preview = document.querySelector('[data-ai-gallery-preview]')?.getBoundingClientRect();

        previewBottom.value = preview === undefined || preview.height === 0 ? undefined : Math.max(0, Math.round(preview.height));
    }

    function forgetPreviewBottom(): void {
        previewBottom.value = undefined;
    }

    const editorPanelStyle = computed<Record<string, string>>(() => {
        if (hasKeyboardBand.value) {
            return {
                maxHeight: `${Math.max(0, Math.round(visibleViewportHeight.value - browserBubbleClearance))}px`,
                bottom: `${keyboardInset.value + browserBubbleClearance}px`,
            };
        }

        // svh, not dvh: Safari regrows its URL bar once the page stops scrolling, and dvh would shrink the panel after layout
        if (previewBottom.value !== undefined) {
            const underThePicture: Record<string, string> = {
                top: `${previewBottom.value}px`,
                height: `calc(100svh - ${previewBottom.value}px)`,
            };

            return underThePicture;
        }

        const beforeMeasuring: Record<string, string> = { maxHeight: fallbackCap };

        return beforeMeasuring;
    });

    return { editorPanelStyle, measurePreviewBottom, forgetPreviewBottom };
}
